import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettings } from '@/settings/settings';
import type { AmbientTranscriptHook, TranscriptSegment } from './useAmbientTranscript';

/**
 * How long each recorded chunk is before it's stopped, uploaded, and the
 * next chunk starts — short enough that suggestions still feel "continuous"
 * (see `useVoiceSuggestions`'s 8s auto-extract cadence, which this feeds),
 * long enough that most single utterances land inside one chunk rather than
 * split across two.
 */
const CHUNK_MS = 6000;

/** Below this, a chunk is silence/near-silence — skip the round trip entirely. */
const MIN_CHUNK_BYTES = 1000;

const CANDIDATE_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return CANDIDATE_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
}

let segmentCounter = 0;
const newSegmentId = () => `wseg_${Date.now().toString(36)}_${(segmentCounter++).toString(36)}`;

interface VoiceTranscribeResponse {
  transcribed: boolean;
  text?: string;
  reason?: string;
}

/**
 * Whisper-backed ambient transcript: records short chunks via MediaRecorder
 * and uploads each to `/api/voice-transcribe` (Azure OpenAI Whisper). Works
 * in any browser with `getUserMedia`/`MediaRecorder` — Firefox and Safari
 * included, unlike the on-device Web Speech API this is preferred over when
 * available (see `useTranscriptSource`) — but needs the server-side Whisper
 * deployment configured, hence the `enabled` gate (from one `/api/health`
 * check, not re-checked per chunk). No interim/partial results — Whisper
 * only returns a finished transcript per chunk, so `interimText` is always
 * empty here; that's a real trade-off against Web Speech's live partials,
 * not an oversight.
 */
export function useWhisperTranscript(enabled: boolean): AmbientTranscriptHook {
  const { settings } = useSettings();

  const mediaSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';
  const available = settings.voice && enabled && mediaSupported;

  const [listening, setListening] = useState(false);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wantListeningRef = useRef(false);
  const mimeTypeRef = useRef<string | undefined>(undefined);

  const uploadChunk = useCallback(async (blob: Blob) => {
    if (blob.size < MIN_CHUNK_BYTES) return;
    try {
      const form = new FormData();
      form.append('audio', blob, 'chunk.webm');
      const res = await fetch('/api/voice-transcribe', {
        method: 'POST',
        body: form,
        signal: AbortSignal.timeout(20000),
      });
      const data = (await res.json()) as VoiceTranscribeResponse;
      if (data.transcribed && data.text) {
        setSegments((prev) => [...prev, { id: newSegmentId(), text: data.text!, at: new Date().toISOString() }]);
      }
    } catch {
      // One chunk failing to upload/transcribe is a transient blip, not a capture
      // failure — the next chunk (a few seconds away) tries again on its own.
      // Surfacing every dropped chunk would be noise, not signal.
    }
  }, []);

  const recordNextChunk = useCallback(() => {
    if (!wantListeningRef.current || !streamRef.current) return;
    const recorder = new MediaRecorder(
      streamRef.current,
      mimeTypeRef.current ? { mimeType: mimeTypeRef.current } : undefined,
    );
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeTypeRef.current ?? 'audio/webm' });
      void uploadChunk(blob);
      if (wantListeningRef.current) recordNextChunk();
    };
    recorderRef.current = recorder;
    recorder.start();
    timeoutRef.current = setTimeout(() => {
      try {
        recorder.stop();
      } catch {
        /* already stopped */
      }
    }, CHUNK_MS);
  }, [uploadChunk]);

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (recorderRef.current) {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore if already stopped */
      }
      recorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!available || wantListeningRef.current) return;
    setError(undefined);
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (!available) {
          // Voice was turned off / became unavailable while the permission prompt was open.
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        mimeTypeRef.current = pickMimeType();
        wantListeningRef.current = true;
        setListening(true);
        recordNextChunk();
      })
      .catch(() => {
        setError('Microphone permission denied — allow mic access in the browser to use voice capture.');
      });
  }, [available, recordNextChunk]);

  const clear = useCallback(() => setSegments([]), []);

  // Stop everything when voice is turned off, or Whisper stops being available, mid-session.
  useEffect(() => {
    if (!available) stop();
  }, [available, stop]);

  // Stop on unmount so a closed panel doesn't leave the mic open.
  useEffect(() => () => stop(), [stop]);

  return { available, listening, segments, interimText: '', start, stop, clear, source: 'whisper', error };
}
